/**
 * Seed data. Mirrors supabase/seed.sql exactly, which in turn mirrors the
 * approved prototype's seed (db.js + seed.js) per FR-12 and Constitution §6.
 *
 * This module is the catalogue the fixtures driver serves when no Supabase
 * project is configured, so the whole platform is browsable before the database
 * exists. When SUPABASE env vars are present the Supabase driver takes over and
 * this file is used only by the pricing vector suite.
 *
 * Prices here are integer cents. The prototype's dollar figures convert once:
 *   $42.94 -> 4294c
 */

import type {
  Brand,
  PrintMethodRow,
  Product,
  PublishedProduct,
  ReadyDesign,
  SiteConfig,
  Tenant,
} from '../types';
import type { PrintMethod } from '../pricing/engine';

/* -------------------------------------------------------------------------- */
/* Brands                                                                      */
/* -------------------------------------------------------------------------- */

export const SEED_BRANDS: Brand[] = [
  {
    id: 'b1000000-0000-4000-8000-000000000001',
    slug: 'as-colour',
    name: 'AS Colour',
    tagline: 'Premium blanks. Tees, hoodies and totes.',
    accent_hex: '#16181d',
    sort: 0,
    active: true,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000002',
    slug: 'biz-collection',
    name: 'Biz Collection',
    tagline: 'Corporate polos and business shirts.',
    accent_hex: '#1466a8',
    sort: 1,
    active: true,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000003',
    slug: 'syzmik',
    name: 'Syzmik',
    tagline: 'Hi-vis, tradie and safety workwear.',
    accent_hex: '#8a3f00',
    sort: 2,
    active: true,
  },
];

/* -------------------------------------------------------------------------- */
/* Blanks                                                                      */
/* -------------------------------------------------------------------------- */

const TEE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const ONE_SIZE = ['One Size'];

export const SEED_PRODUCTS: Product[] = [
  {
    id: 'p1000000-0000-4000-8000-000000000001',
    name: 'Mens Hi-Vis Cotton Tee',
    category: 'Hi-Vis Workwear',
    type: 'tee',
    base_price_cents: 1701,
    colors: [
      { name: 'Hi-Vis Yellow', hex: '#e9e200' },
      { name: 'Hi-Vis Orange', hex: '#ff6a00' },
      { name: 'Navy', hex: '#22314f' },
    ],
    sizes: TEE_SIZES,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000002',
    name: 'Argent Mens S/S Polo',
    category: 'Polos',
    type: 'polo',
    base_price_cents: 4294,
    colors: [
      { name: 'White', hex: '#f5f5f5' },
      { name: 'Black', hex: '#1c1c1c' },
      { name: 'Bottle Green', hex: '#1d4d3b' },
    ],
    sizes: TEE_SIZES,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000003',
    name: 'Classic Pullover Hoodie',
    category: 'Tees & Hoodies',
    type: 'hoodie',
    base_price_cents: 3850,
    colors: [
      { name: 'Charcoal', hex: '#3a3d42' },
      { name: 'Maroon', hex: '#5e1f2c' },
      { name: 'Sky Blue', hex: '#7fb6d9' },
    ],
    sizes: TEE_SIZES,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000004',
    name: 'Premium Cotton Tee',
    category: 'Tees & Hoodies',
    type: 'tee',
    base_price_cents: 1995,
    colors: [
      { name: 'White', hex: '#f5f5f5' },
      { name: 'Black', hex: '#1c1c1c' },
      { name: 'Red', hex: '#c0322b' },
      { name: 'Royal', hex: '#2c4bb0' },
    ],
    sizes: TEE_SIZES,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000005',
    name: 'Structured Baseball Cap',
    category: 'Caps & Hats',
    type: 'cap',
    base_price_cents: 1420,
    colors: [
      { name: 'Black', hex: '#1c1c1c' },
      { name: 'Navy', hex: '#22314f' },
      { name: 'Stone', hex: '#d8cdb8' },
    ],
    sizes: ONE_SIZE,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000006',
    name: 'Cotton Canvas Tote Bag',
    category: 'Tote Bags & Towels',
    type: 'tote',
    base_price_cents: 980,
    colors: [
      { name: 'Natural', hex: '#e7ddc6' },
      { name: 'Black', hex: '#1c1c1c' },
    ],
    sizes: ONE_SIZE,
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000007',
    name: 'Long-Sleeve Fishing Shirt',
    category: 'Fishing Wear',
    type: 'polo',
    base_price_cents: 2650,
    colors: [
      { name: 'Sky Blue', hex: '#7fb6d9' },
      { name: 'White', hex: '#f5f5f5' },
      { name: 'Sand', hex: '#d8cdb8' },
    ],
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    active: true,
  },
  {
    id: 'p1000000-0000-4000-8000-000000000008',
    name: 'Tradie Hi-Vis Work Polo',
    category: 'Workwear',
    type: 'polo',
    base_price_cents: 2890,
    colors: [
      { name: 'Hi-Vis Yellow', hex: '#e9e200' },
      { name: 'Hi-Vis Orange', hex: '#ff6a00' },
      { name: 'Navy', hex: '#22314f' },
    ],
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
    active: true,
  },
];

/* -------------------------------------------------------------------------- */
/* Decoration methods, seeded from specs/shared/pricing-engine.md §1           */
/* -------------------------------------------------------------------------- */

/**
 * The four methods this client sells, in the order they lead with them.
 * Embroidery is first because it is the core of the business,
 * not because it sorts that way alphabetically.
 *
 * Changed from the June set: DTG and Heat Transfer are out, DTF and Sublimation
 * are in. Sublimation was parked as a later addition in the original proposal
 * and has been pulled forward — the site sells it today (spec 002 FR-23).
 *
 * Rates below are placeholders pending Abdul's real numbers, which is Discovery
 * task T-P0.1. They are plausible, not quoted.
 */
export const SEED_PRINT_METHOD_ROWS: PrintMethodRow[] = [
  {
    key: 'embroidery',
    name: 'Embroidery',
    blurb: 'Premium stitched finish. Extremely durable, and the shop speciality.',
    moq: 1,
    max_colours: 12,
    turnaround: '1-2 weeks',
    // Digitising is charged once per design and remembered, so a reorder of the
    // same logo does not pay setup twice.
    setup_per_colour_per_location_cents: 0,
    setup_per_location_cents: 4000,
    print_per_location_cents: 700,
    extra_colour_cost_cents: 20,
    specialty: 'Digitising charged once, then free on reorders',
    sort: 0,
    active: true,
  },
  {
    key: 'screen',
    name: 'Screen Printing',
    blurb: 'Bold, ultra wash-resistant prints. Best value at higher volumes.',
    moq: 24,
    max_colours: 6,
    turnaround: '1-2 weeks',
    setup_per_colour_per_location_cents: 2500,
    setup_per_location_cents: 0,
    print_per_location_cents: 180,
    extra_colour_cost_cents: 45,
    specialty: null,
    sort: 1,
    active: true,
  },
  {
    key: 'dtf',
    name: 'DTF Printing',
    blurb: 'Full colour, no setup and no minimum. Ideal for small runs.',
    moq: 1,
    max_colours: null,
    turnaround: '1-3 days',
    setup_per_colour_per_location_cents: 0,
    setup_per_location_cents: 0,
    print_per_location_cents: 650,
    extra_colour_cost_cents: 0,
    specialty: null,
    sort: 2,
    active: true,
  },
  {
    key: 'sublimation',
    name: 'Sublimation',
    blurb: 'All-over colour on performance fabric. Built for team kits.',
    moq: 10,
    max_colours: null,
    turnaround: '2-3 weeks',
    setup_per_colour_per_location_cents: 0,
    setup_per_location_cents: 0,
    print_per_location_cents: 550,
    extra_colour_cost_cents: 0,
    specialty: 'Prints the whole garment, not just a panel',
    sort: 3,
    active: true,
  },
];

/** The engine works in dollars for prototype parity; convert at the boundary. */
export function methodRowToEngine(row: PrintMethodRow): PrintMethod {
  return {
    key: row.key,
    name: row.name,
    blurb: row.blurb,
    moq: row.moq,
    max_colours: row.max_colours,
    turnaround: row.turnaround,
    setup_per_colour_per_location: row.setup_per_colour_per_location_cents / 100,
    setup_per_location: row.setup_per_location_cents / 100,
    print_per_location: row.print_per_location_cents / 100,
    extra_colour_cost: row.extra_colour_cost_cents / 100,
    specialty: row.specialty,
  };
}

/** Engine-shaped methods, used directly by the vector suite. */
export const SEED_PRINT_METHODS: PrintMethod[] =
  SEED_PRINT_METHOD_ROWS.map(methodRowToEngine);

/* -------------------------------------------------------------------------- */
/* Ready-made designs                                                          */
/* -------------------------------------------------------------------------- */

export const SEED_READY_DESIGNS: ReadyDesign[] = [
  {
    id: 'd1000000-0000-4000-8000-000000000001',
    name: 'Tribal Wave',
    image_url: '/designs/template1.png',
    sort: 0,
    active: true,
  },
  {
    id: 'd1000000-0000-4000-8000-000000000002',
    name: 'Red Dragon',
    image_url: '/designs/template2.png',
    sort: 1,
    active: true,
  },
  {
    id: 'd1000000-0000-4000-8000-000000000003',
    name: 'Line Skull',
    image_url: '/designs/template3.png',
    sort: 2,
    active: true,
  },
];

/* -------------------------------------------------------------------------- */
/* Published retail products                                                   */
/* -------------------------------------------------------------------------- */

const hexFor = (productId: string, colorName: string): string => {
  const p = SEED_PRODUCTS.find((x) => x.id === productId);
  return p?.colors.find((c) => c.name === colorName)?.hex ?? '#cccccc';
};

interface PublishSpec {
  id: string;
  title: string;
  brand: string;
  product: string;
  color: string;
  method: string;
  colours: number;
  design: string;
  scale: number;
  /** Cost to produce one unit, in cents. */
  base_cost_cents: number;
  retail_price_cents: number;
  fulfilment: 'pod' | 'stock';
  stock_qty: number | null;
}

const PUBLISH_SPECS: PublishSpec[] = [
  {
    id: 'a1000000-0000-4000-8000-000000000001',
    title: 'Corporate Long-Sleeve Polo',
    brand: 'biz-collection',
    product: 'p1000000-0000-4000-8000-000000000007',
    color: 'Sky Blue',
    method: 'dtf',
    colours: 1,
    design: '/designs/template1.png',
    scale: 58,
    base_cost_cents: 3300,
    retail_price_cents: 4995,
    fulfilment: 'stock',
    stock_qty: 120,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000002',
    title: 'Team Logo Tee',
    brand: 'biz-collection',
    product: 'p1000000-0000-4000-8000-000000000004',
    color: 'Royal',
    method: 'dtf',
    colours: 1,
    design: '/designs/template3.png',
    scale: 60,
    base_cost_cents: 2645,
    retail_price_cents: 3995,
    fulfilment: 'pod',
    stock_qty: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000003',
    title: 'Crew Logo Tee',
    brand: 'as-colour',
    product: 'p1000000-0000-4000-8000-000000000004',
    color: 'Black',
    method: 'dtf',
    colours: 1,
    design: '/designs/template2.png',
    scale: 62,
    base_cost_cents: 2645,
    retail_price_cents: 3495,
    fulfilment: 'pod',
    stock_qty: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000004',
    title: 'Event Print Tee',
    brand: 'as-colour',
    product: 'p1000000-0000-4000-8000-000000000004',
    color: 'White',
    method: 'screen',
    colours: 2,
    design: '/designs/template1.png',
    scale: 60,
    base_cost_cents: 2220,
    retail_price_cents: 3795,
    fulfilment: 'stock',
    stock_qty: 64,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000005',
    title: 'Branded Pullover Hoodie',
    brand: 'syzmik',
    product: 'p1000000-0000-4000-8000-000000000003',
    color: 'Charcoal',
    method: 'screen',
    colours: 2,
    design: '/designs/template2.png',
    scale: 58,
    base_cost_cents: 4075,
    retail_price_cents: 6995,
    fulfilment: 'pod',
    stock_qty: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000006',
    title: 'Embroidered Trucker Cap',
    brand: 'syzmik',
    product: 'p1000000-0000-4000-8000-000000000005',
    color: 'Navy',
    method: 'embroidery',
    colours: 4,
    design: '/designs/template1.png',
    scale: 70,
    base_cost_cents: 2180,
    retail_price_cents: 3295,
    fulfilment: 'stock',
    stock_qty: 45,
  },
];

export const SEED_PUBLISHED_PRODUCTS: PublishedProduct[] = PUBLISH_SPECS.map((s) => {
  const blank = SEED_PRODUCTS.find((p) => p.id === s.product)!;
  const brand = SEED_BRANDS.find((b) => b.slug === s.brand)!;
  return {
    id: s.id,
    title: s.title,
    brand_id: brand.id,
    product_id: blank.id,
    type: blank.type,
    color_name: s.color,
    color_hex: hexFor(blank.id, s.color),
    design_url: s.design,
    method_key: s.method,
    colours: s.colours,
    locations: ['front'],
    pos_x: 50,
    pos_y: blank.type === 'cap' ? 50 : 48,
    scale: s.scale,
    base_cost_cents: s.base_cost_cents,
    retail_price_cents: s.retail_price_cents,
    fulfilment: s.fulfilment,
    stock_qty: s.stock_qty,
    active: true,
    created_at: '2026-06-11T02:00:00.000Z',
  };
});

/* -------------------------------------------------------------------------- */
/* Site config, FR-15                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The tenant the fixtures describe. R-140.
 *
 * These are the values the real `tenant_config` holds for this client, kept
 * here so fixtures mode shows the same shop name as production instead of a
 * placeholder that then leaks into a screenshot.
 */
export const SEED_TENANT: Tenant = {
  name: 'Obsessed Gymwear',
  system_name: 'ESSE',
  system_logo_url: '/esse-logo.png',
  legal_name: 'OBSESSED ENTERPRISE PTY LTD',
  accent_hex: '#111111',
  // The same file the database points at, so the fixtures walkthrough shows the
  // client's own header rather than a fallback nobody will ever ship.
  logo_url: '/obsessed-gymwear-logo.png',
  email_from: 'Obsessed Gymwear <orders@obsessedgymwear.com.au>',
  domain: 'obsessedgymwear.com.au',
  currency: 'AUD',
  gst_rate: 0.1,
  timezone: 'Australia/Brisbane',
};

export const SEED_SITE_CONFIG: SiteConfig = {
  // Off until the apps ship. AC-6.1 / AC-6.2 hang off this one flag.
  apps_live: false,
  shipping_flat_cents: 1200,
  free_shipping_threshold_cents: 15000,
  app_only_promo_code: 'APPLAUNCH15',
  app_only_promo_label: 'App-exclusive launch offer, 15% off the first app order',
};
